import { useState, useEffect, useCallback, useId, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Modal } from "@shared/controls/Modal";
import { PrimaryButton } from "@shared/buttons";

import { getProfile, login, googleSignup } from "@services/krent.service";
import { addCookie } from "@utils/cookies";
import Logo from "@shared/Logo";
import { GoogleSignIn } from "@shared/GoogleSignIn";
import {
  persistGoogleSession,
  GoogleAuthResult,
} from "@utils/completeGoogleAuth";
import {
  goToPostAuthDestination,
  persistProfileUser,
  resolvePostAuthDestination,
  savePostLoginRedirect,
} from "@utils/postAuthRedirect";
import { createUserInterest } from "@services/krentDeveloper";
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { firestore } from "@services/firebase.service";

interface LoginModalProps {
  show: boolean;
  onClose: () => void;
  onOpenSignup?: () => void;
  disableNavigation?: boolean;
  onLoginSuccess?: () => void;
}

export const LoginModal = ({
  show,
  onClose,
  onOpenSignup,
  disableNavigation = false,
  onLoginSuccess,
}: LoginModalProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [togglePassword, setTogglePassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  // Unique ids per instance — multiple LoginModals can mount (navbar + price card, etc.)
  const reactId = useId();
  const stableId = reactId.replace(/:/g, "");
  const modalId = `loginModal-${stableId}`;
  const passwordInputId = `login-password-${stableId}`;
  const googleButtonId = `google-signin-button-modal-${stableId}`;
  const googleContainerRef = useRef<HTMLDivElement>(null);
  const [googleButtonWidth, setGoogleButtonWidth] = useState(320);

  const finishAuth = useCallback((profileUser?: any) => {
    const dest = resolvePostAuthDestination(profileUser);
    onClose();
    goToPostAuthDestination(navigate, dest);
  }, [navigate, onClose]);

  const [loading, setLoading] = useState<boolean>(false);
  const [googleLoading, setGoogleLoading] = useState<boolean>(false);
  const messageCollections: any = process.env["REACT_APP_FIREBASE_COLLECTION"];

  const abortController = new AbortController();

  // Add CSS for loading dots animation
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes bounce {
        0%, 80%, 100% { 
          transform: scale(0);
        } 40% { 
          transform: scale(1);
        }
      }
      .loading-dots {
        display: inline-flex;
        gap: 4px;
        align-items: center;
      }
      .loading-dots span {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #05569f;
        animation: bounce 1.4s infinite ease-in-out both;
      }
      .loading-dots span:nth-child(1) {
        animation-delay: -0.32s;
      }
      .loading-dots span:nth-child(2) {
        animation-delay: -0.16s;
      }
      .loading-dots span:nth-child(3) {
        animation-delay: 0s;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    if (show && /^\/property\/[^/]+\/?$/.test(window.location.pathname)) {
      savePostLoginRedirect();
    }
  }, [show]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setEmail("");
      setPassword("");
      setError("");
      setTogglePassword(false);
    }
  }, [show]);

  // Size Google button to the container so a fixed 400px width doesn't overflow and jitter
  useEffect(() => {
    if (!show) return;

    const el = googleContainerRef.current;
    if (!el) return;

    const updateWidth = () => {
      const next = Math.max(200, Math.floor(el.clientWidth));
      setGoogleButtonWidth((prev) => (prev === next ? prev : next));
    };

    updateWidth();
    // Modal may still be display:none on first paint; remeasure after Bootstrap shows it
    const timeoutId = window.setTimeout(updateWidth, 100);
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => {
      window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [show]);

  const handleGoogleSignIn = useCallback(
    async (response: any) => {
      try {
        setGoogleLoading(true);
        setError("");

        const loginRes = (await googleSignup({
          id_token: response.credential,
        })) as GoogleAuthResult;

        if (!loginRes.success || !loginRes.token) {
          throw new Error("Google signin failed");
        }

        const profileUser = await persistGoogleSession(loginRes);

        if (disableNavigation) {
          onClose();
          onLoginSuccess?.();
          setTimeout(() => {
            window.location.reload();
          }, 300);
          return;
        }

        finishAuth(profileUser || loginRes);
      } catch (err: any) {
        const errorMessage =
          err?.message || "Google signin failed. Please try again.";
        setError(errorMessage);
      } finally {
        setGoogleLoading(false);
      }
    },
    [
      finishAuth,
      disableNavigation,
      navigate,
      onClose,
      onLoginSuccess,
    ],
  );

  const interestId =
    JSON.parse(localStorage.getItem("InterestIdReroute") as any) || "";
  const agentId = localStorage.getItem("agentId") as any | "";

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await login(email, password);

      if (res["success"] === true) {
        addCookie("1Q_SPA", res["token"], 24000);

        const d: any = await getProfile(abortController.signal);
        persistProfileUser(d?.data || d);

        if (disableNavigation) {
          onClose();
          onLoginSuccess?.();
          // Force page refresh to update UI
          setTimeout(() => {
            window.location.reload();
          }, 300);
          return;
        }

        if (sessionStorage.getItem("pendingBooking") || localStorage.getItem("postLoginRedirect")) {
          finishAuth(d?.data || d);
          return;
        }

        if (
          d?.data?.role === "landlord" ||
          d?.data?.role === "agent" ||
          d?.data?.role === "property-manager"
        ) {
          onClose();
          navigate("/overview", { replace: true });
        } else if (d?.data?.role === "user") {
          if (interestId) {
            const response = await createUserInterest({
              propertyId: interestId,
              agentId,
            });

            if (response.success) {
              const newInterestId = response?.data?._id;

              await setDoc(
                doc(collection(firestore, messageCollections), newInterestId),
                {
                  meta: {
                    request: {
                      id: newInterestId,
                      property: interestId,
                      request_type: "interest",
                      source: "developer",
                    },
                    sent_by: {
                      name:
                        d?.data?.prefferredName ||
                        `${d?.data?.firstname} ${d?.data?.lastname}`,
                      photoUrl: d?.data?.photo,
                    },
                    owner: {
                      name:
                        response.data.agent.firstname +
                        " " +
                        response.data.agent.lastname,
                    },
                  },
                  chats: [
                    {
                      from_uid: d?.data?.id,
                      to_uid: response.data.agent.id,
                      type: "text",
                      value: "I'm interested in this developer property",
                      createdAt: Timestamp.now(),
                    },
                  ],
                  read: false,
                  members: [response.data.agent.id, d?.data?.id],
                  createdAt: serverTimestamp(),
                },
              );

              onClose();
              navigate("/purchases", { replace: true });
            } else {
              setError("Failed to create interest");
            }
          } else {
            onClose();
            navigate("/explore-listings", { replace: true });
          }
        } else if (d?.data?.role === "developer") {
          onClose();
          navigate("/developer-dashboard", { replace: true });
        }
      }
    } catch (err: any) {
      console.error("Login error:", err);
      setError(err?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      size={500}
      id={modalId}
      className="login-modal"
    >
      <div
        className="px-4 pb-3"
        style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}
      >
        <div className="d-flex  mb-3">
          <Logo />
        </div>

        <h4 className="fw-bold font-onest ">Welcome back 👋</h4>
        <p className="text-light-black fs-14 mb-4 font-onest">
          We've missed you! Sign in to pick up right where you left off.
        </p>

        <form className="mt-4 mt-md-3" onSubmit={handleSubmit}>
          {error && <span className="text-danger">{error}</span>}
          <div className="col mb-4">
            <label className="text-uppercase fw-600 fs-12 m-1">
              Email Address
            </label>
            <input
              type="email"
              className="form-control border rounded-3 ps-3 py-3 fs-14"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              name="email"
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div className="form-group col mb-3">
            <label
              className="text-uppercase fw-600 fs-12 m-1"
              htmlFor={passwordInputId}
            >
              Password
            </label>
            <div className="input-group">
              <span
                className="input-group-text input-group-text-0 "
                id={`${passwordInputId}-addon`}
              >
                <i className={`iconly-Lock icli fs-4`}></i>
              </span>

              <input
                type={togglePassword ? "text" : "password"}
                className="form-control form-control-0 ps-3 py-3 border-start-0"
                id={passwordInputId}
                name="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                style={{ borderRight: "none" }}
              />

              <span
                className="input-group-text input-group-text-0"
                role="button"
                tabIndex={loading ? -1 : 0}
                aria-label={togglePassword ? "Hide password" : "Show password"}
                onClick={() => !loading && setTogglePassword((prev) => !prev)}
                onKeyDown={(e) => {
                  if (!loading && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    setTogglePassword((prev) => !prev);
                  }
                }}
              >
                <i
                  className={`${togglePassword ? "iconly-Show" : "iconly-Hide"} icli fs-4`}
                  style={{ cursor: "pointer", color: "#05569f" }}
                  aria-hidden="true"
                />
              </span>
            </div>
          </div>

          <div className="row flex mb-4">
            {/* <div className="col">
              <span
                className="text-light-black fs-14 text-link"
                style={{ cursor: "pointer", color: "#47BBA9" }}
                onClick={() => {
                  onClose();
                  navigate("/reverify-email/");
                }}
              >
                Resend Verification Email
              </span>
            </div> */}
            <div className="col text-end">
              <span
                className="text-light-black fs-14 text-link"
                style={{ cursor: "pointer", color: "#47BBA9" }}
                onClick={() => {
                  onClose();
                  navigate("/forgot-password/");
                }}
              >
                Forgot password?
              </span>
            </div>
          </div>

          <div className="col text-center">
            <PrimaryButton type="primary" text="Sign In" loading={loading} />
          </div>
        </form>

        <p className="text-light-black text-center my-4 my-md-3 fs-14">
          Don't have an account?{" "}
          <span
            onClick={() => {
              onOpenSignup?.();
              onClose();
            }}
            className="text-link"
            style={{ cursor: "pointer", color: "#47BBA9" }}
          >
            Register here
          </span>
        </p>

        <div className="text-center my-4">
          <span className="text-muted fs-14">OR</span>
        </div>

        <div className="d-grid">
          <div
            className="d-flex align-items-center justify-content-center p-2 rounded-3 position-relative w-100"
            style={{
              minHeight: "52px",
              backgroundColor: "#ffffff",
              overflow: "hidden",
            }}
          >
            <div
              ref={googleContainerRef}
              className="w-100"
              style={{ overflow: "hidden" }}
            >
              <GoogleSignIn
                onSuccess={handleGoogleSignIn}
                onError={setError}
                buttonId={googleButtonId}
                text="signin_with"
                width={googleButtonWidth}
                enabled={show}
              />
            </div>
            {googleLoading && (
              <div className="position-absolute d-flex align-items-center justify-content-center w-100">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
